/* @flow */
import { action, computed, observable } from 'mobx';

import { ApiModel } from 'api/ApiModel';

async function fetchDetails(id: string, api: Object): Promise<*> {
  return {
    details: await api.getResource('accounts/' + id),
  };
}

const checkTypeOfUndefined: Function = (value: any) => (typeof value === 'undefined' ? [] : value);

const accountUrls = {
  documentAndResources: 'accounts/documents/',
  deleteFile: (accountId, fileId) => `accounts/${accountId}/documents/${fileId}`,
  downloadDocumentsAndResources: '/accounts/documents/',
};

export type DocumentOrResource = {|
  id: number,
  file: string,
  type: string,
  name: string,
  created_at: string,
|};

export default class AccountStore extends ApiModel {
  @observable accountId: string;
  @observable details: any = null;
  @observable allUsersByAccount: Object;
  @observable users: Array<*> = [];
  @observable specificUser: Object = null;
  @observable managers: Array<*> = [];
  @observable purchasers: Array<*> = [];
  @observable customerViewOnly: Array<*> = [];
  @observable admins: Array<*> = [];
  @observable locations: Array<*> = [];
  @observable locationColumn: Number = 0;
  @observable loadingDetails: boolean = true;
  @observable loadingLocations: boolean = false;
  @observable loadingUsers: boolean = false;
  @observable loadingAccountActivity: boolean = false;
  @observable error: boolean = false;
  @observable errorMessage: Object;
  @observable searchType = 'rental';
  @observable isCaasActive: boolean = false;

  @action
  async loadDetails(accountId: string, load: boolean = true): Promise<void> {
    this.error = false;
    this.accountId = accountId;
    this.loadingDetails = load;

    try {
      this.details = await fetchDetails(accountId, this.api);
    } catch (err) {
      this.error = true;
      this.errorMessage = this.api.oAuthStore.errorResponse.message;
    }

    this.loadingDetails = false;
  }

  @action
  async loadUsers(accountId: string, params: Array = []): Promise<void> {
    this.loadingUsers = true;

    let allUsers: Object;

    try {
      allUsers = await this.api.getResource(
        `accounts/${accountId}/users?search=${params.join(',')}`
      );
    } catch (err) {
      console.log(err);
      allUsers = {};
    }

    this.allUsersByAccount = allUsers;
    this.users = checkTypeOfUndefined(allUsers.user);
    this.managers = checkTypeOfUndefined(allUsers.manager);
    this.purchasers = checkTypeOfUndefined(allUsers.purchaser);
    this.customerViewOnly = checkTypeOfUndefined(allUsers.customer_view_only);
    this.admins = checkTypeOfUndefined(allUsers.account_admin);

    this.loadingUsers = false;
  }

  @action
  async loadSpecificUser(id: string, accountId: string): Promise<void> {
    this.loadingUsers = true;

    try {
      this.specificUser = await this.api.getResource(`users/${id}?account_id=${accountId}`);
    } catch (err) {
      console.log(err);
      // this.specificUser = {};
    }

    this.loadingUsers = false;
  }

  assignAccount = (data: *) => {
    return this.api.createResource(`accounts/${this.details.details.id}/assign-users`, data);
  };

  @action
  async loadRecentActivity(id: string, params: ?string = 'page_size=1000'): Promise<void> {
    this.loadingAccountActivity = true;
    const urlParams: string = params ? '/activity?' + params : '/activity';

    try {
      const res: ?Array<*> = await this.api.getResource('accounts/' + id + urlParams);
      this.accountActivity = res;
    } catch (e) {
      console.log(e, 'catching ERRORS');
    }

    this.loadingAccountActivity = false;
  }

  resetUsers = () => {
    this.users = [];
    this.managers = [];
    this.purchasers = [];
    this.customerViewOnly = [];
    this.admins = [];
    this.specificUser = {};
    this.accountActivity = {};
    this.loadingDetails = false;
    this.loadingUsers = false;
    this.loadingAccountActivity = false;
  };

  //----------------------------------------------------------------------//
  // MANAGE LOCATIONS                                                     //
  //----------------------------------------------------------------------//

  @observable loadingResources: boolean = false;

  @observable locationLists: Array<*> = [];

  @observable locationSelections: Array<*> = [];

  @observable companyDetails: ?Object = null;

  @observable countryDetails: ?Object = null;

  @observable locationDetails: ?Object = null;

  @action
  async loadResource(path: string): * {
    this.loadingResources = true;

    const resource: Promise<*> = await this.api
      .getResource(path)
      .then((success: *) => success)
      .catch((error: *) => this.errorMessage);

    this.loadingResources = false;

    if ((Array.isArray(resource) && !resource.length) || resource === null) {
      return this.errorMessage;
    }

    return resource && resource.length ? resource : [resource];
  }

  @action
  async loadCompanies(accountId: string, searchTerms: Array<string> = []): Promise<void> {
    const { searchType } = this;

    // List of values that will be joined with "&" to form the query string for the endpoint.
    const queryString: Array<string> = [];

    // List of values to form the "search" parameter for the query string, these values
    // will be joined with ";" to form a single value for "search" parameter.
    const searchValues: Array<string> = [];

    if (this.isCaasActive) {
      queryString.push('caas=true');
    }

    if (searchTerms.length > 0) {
      searchValues.push(searchTerms.join(','));
    }

    // Search values for rush asset and regular asset request sites.
    switch (searchType) {
      case 'asset-request':
        searchValues.push('asset_request_location:true');
        searchValues.push('asset_request_location_type:customer|delivery');
        break;

      case 'rush-asset-request':
        searchValues.push('rush_asset_request_location:1');
        searchValues.push('rush_asset_request_location_type:customer|delivery');
        break;

      default:
        break;
    }

    if (searchValues.length > 0) {
      queryString.push('search=' + searchValues.join(';'));
    }

    const companies: * = await this.loadResource(
      `accounts/${accountId}/companies?${queryString.join('&')}`
    );

    this.resetLocations();

    this.locationLists.push(companies);
  }

  @action
  async loadCompanyDetails(companyId: string): Promise<void> {
    const companyDetails: * = await this.loadResource(`companies/${companyId}`);

    this.companyDetails = companyDetails.map(item => ({ ...item, company_name: item.name }))[0];
  }

  async loadCountries(companyId: string): Promise<void> {
    const countries: * = await this.loadResource(`companies/${companyId}/countries`);

    return countries;
  }

  @action
  async loadCountryDetails(companyId: string, countryId: string): Promise<void> {
    const countryDetails: * = await this.loadResource(
      `companies/${companyId}/countries/${countryId}`
    );

    this.countryDetails = countryDetails.map(item => ({ ...item, country_name: item.name }))[0];
  }

  async loadLocations(siteId: string): Promise<void> {
    const isAssetRequestType: boolean = this.searchType === 'asset-request';

    const searchFields: string = isAssetRequestType
      ? `&search=asset_request_location:true;asset_request_location_type:customer|delivery`
      : '';

    if (!this.locationSelections[1]) {
      return null;
    }

    const sites: * = await this.loadResource(
      `locations/${this.locationSelections[0].id}/${
        this.locationSelections[1].id
      }?parent_id=${siteId}${searchFields}`
    );

    return sites;
  }

  @action
  async loadLocationDetails(locationId: string): Promise<void> {
    const locationDetails: * = await this.loadResource(`locations/${locationId}`);

    if (locationDetails) {
      this.locationDetails = locationDetails.map(item => ({
        ...item,
        location_name: item.name,
      }))[0];
    }
  }

  @action
  async updateLocations(selected: any, index: number, load: boolean = true): Promise<void> {
    this.loadingLocations = true;

    while (this.locationSelections.length > index) {
      if (this.locationSelections[index] && selected) {
        if (this.locationSelections[index].id === selected.id && load) {
          this.loadingLocations = false;
          return null;
        }
      }

      this.locationSelections.pop();
      this.locationLists.pop();
    }

    this.locationSelections.push(selected);

    if (index === 0) {
      this.locationLists.push(await this.loadCountries(selected.id));
    } else if (index > 0 && index < 5) {
      const siteId: string =
        this.locationSelections.length > 2 ? this.locationSelections[index].id : '';
      this.locationLists.push(await this.loadLocations(siteId));
    } else {
      this.loadingLocations = false;
      return this;
    }

    this.loadingLocations = false;
    return this;
  }

  @computed get getLocationLists(): Array<Array<*>> {
    return this.locationLists;
  }

  @action resetLocations = () => {
    this.locationLists = [];
    this.locationSelections = [];
    this.companyDetails = null;
    this.countryDetails = null;
    this.locationDetails = null;
    this.loadingResources = false;
  };

  @action resetStore = () => {
    this.resetUsers();
    //this.resetLocations();
    this.accountId = '';
    this.details = null;
  };

  //----------------------------------------------------------------------//
  // MANAGE DOCUMENTS & RESOURCES                                         //
  //----------------------------------------------------------------------//

  @observable documentsAndResources: Array<*> = [];

  @action
  async loadDocumentsAndResources(id) {
    try {
      this.documentsAndResources = await this.api.getResource(
        accountUrls.documentAndResources + id + '/get-files'
      );
    } catch (e) {
      console.log(e, 'catching ERRORS');
    }
  }

  @action
  async onDeleteFile(accountId, fileId) {
    try {
      const url = accountUrls.deleteFile(accountId, fileId);
      const res = await this.api.deleteResource(url);

      this.documentsAndResources = res.original.data;
    } catch (e) {
      console.log(e, 'catching ERRORS');
    }
  }

  @computed
  get getDocumentsAndResources(): Array<*> {
    const accountId = this.accountId;

    return this.documentsAndResources.map(file => {
      return {
        id: file.id,
        name: file.name,
        type: file.type,
        showType: false,
        icon: '#icon-contracts',
        created_at: file.created_at,
        folderId: accountId,
        link: accountUrls.downloadDocumentsAndResources + file.id,
        onDelete: this.onDeleteFile.bind(this),
      };
    });
  }
}

export type TypeAccountStore = {|
  details: Object,
  users: Array<*>,
  specificUser: Object,
  loadingUsers: boolean,
  locations: Array<*>,
  accountActivity: ?Array<*>,
  loadDetails: () => {},
  loadUsers: (id: string) => {},
  loadSpecificUser: (id: string) => {},
  loadRecentActivity: () => {},
|};
